// v2 mountain goat — a crag climber in a deep winter coat: a shaggy body with a
// pronounced shoulder hump and a hanging fur skirt, sturdy legs ending in dark
// cliff-gripping hooves, and the signature head — a long pale face with the
// prominent chin beard and two black back-curved horns built from bent segments
// so they really sweep. Two coats: bright white and aged cream. Ported 1:1 from
// age-of-mcp's v2/mountain-goat.ts, using the library's shared `critter`
// (quadLegs) and `wildland` (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, critter, wildland, type Part } from "../../lib";

const COATS = [
  { coat: "#f1f2ec", skirt: "#e0e1d6", face: "#f7f8f3", muzzle: "#e6e7de",
    leg: "#e8e9e0", horn: "#2e2a24", hoof: "#26221c" },
  { coat: "#e9e6da", skirt: "#d6d2c2", face: "#f1eee2", muzzle: "#ddd9ca",
    leg: "#ddd9c9", horn: "#332e26", hoof: "#2a2620" },
];

function mountainGoatParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.052, dz: 0.027, bodyY: 0.105, rTop: 0.0095, rFoot: 0.007,
    color: c.leg, hoof: { color: c.hoof, r: 0.0095, h: 0.015 },
  });
  parts.push(
    // shaggy torso + shoulder hump + hanging fur skirt + rump
    { geometry: new THREE.IcosahedronGeometry(0.052, 0), color: c.coat,
      position: [0, 0.105, 0], scale: [1.6, 1.0, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: c.coat,
      position: [0.035, 0.131, 0], scale: [1.05, 0.9, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.skirt,
      position: [-0.005, 0.085, 0], scale: [1.55, 0.7, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.036, 0), color: c.coat,
      position: [-0.055, 0.108, 0], scale: [1, 0.95, 0.9] },
    // neck and long pale face
    wildland.tube([0.072, 0.122, 0], [0.096, 0.148, 0], 0.02, 0.016, c.coat),
    { geometry: new THREE.IcosahedronGeometry(0.022, 0), color: c.face,
      position: [0.102, 0.152, 0], scale: [1.3, 1.0, 0.85] },
    { geometry: new THREE.ConeGeometry(0.0095, 0.024, 4), color: c.muzzle,
      position: [0.128, 0.146, 0], rotation: [0, 0, -Math.PI / 2] },
    { geometry: new THREE.IcosahedronGeometry(0.0035, 0), color: c.hoof,
      position: [0.14, 0.146, 0] },
    // the chin beard
    { geometry: new THREE.ConeGeometry(0.0085, 0.026, 4), color: c.skirt,
      position: [0.118, 0.128, 0], rotation: [Math.PI, 0, 0] },
    // short tail
    { geometry: new THREE.IcosahedronGeometry(0.009, 0), color: c.skirt,
      position: [-0.085, 0.118, 0] },
  );
  for (const s of [1, -1] as const) {
    // ears out sideways + black horns swept back in two bent segments
    parts.push(
      { geometry: new THREE.ConeGeometry(0.006, 0.016, 4), color: c.face,
        position: [0.092, 0.166, s * 0.017], rotation: [s * 1.2, 0, 0] },
      wildland.tube([0.098, 0.163, s * 0.012], [0.089, 0.176, s * 0.016],
        0.0042, 0.0032, c.horn),
      wildland.tube([0.089, 0.176, s * 0.016], [0.075, 0.181, s * 0.0185],
        0.0032, 0.0012, c.horn),
    );
  }
  parts.push(...wildland.eyes(0.108, 0.159, 0.0125, 0.0035, "#1c180f"));
  return parts;
}

export const mountainGoatV2 = defineModel({
  id: "mountain-goat-v2",
  scale: 2.16, // real-world size correction (see Model.scale)
  batchId: "fable",
  name: "Mountain goat (v2)",
  tags: ["creature","mountain","goat","low-poly","age-of-mcp","fable"],
  summary: "Low-poly mountain goat.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.13,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => mountainGoatParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => mountainGoatParts(1) },
  ],
  animations: [],
});
